// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db, functions } from '../firebase'; // Your initialized Firebase auth instance
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Callable to sync user profile data (fire and forget on sign-in)
const syncUserProfileCallable = httpsCallable(functions, 'syncUserProfile');

// Only sync profile if lastRefreshed is older than 24 hours
const SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

// Create the context
const AuthContext = createContext();

// Custom hook to use the AuthContext easily
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component definition
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userOrgId, setUserOrgId] = useState(null);
  const [userFidClaim, setUserFidClaim] = useState(null);
  const [userMidClaim, setUserMidClaim] = useState(null);
  const [memberPhoto, setMemberPhoto] = useState(null);
  const [memberName, setMemberName] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true); // Loading state for auth check

  useEffect(() => {
    let cancelled = false;

    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;
      setCurrentUser(user); // Set the user object (or null)

      if (user) {
        // If user is logged in, get their ID token result to access custom claims
        try {
          const idTokenResult = await user.getIdTokenResult(false);
          if (cancelled) return;
          const claims = idTokenResult.claims;
          console.log("Auth Provider Claims:", claims);

          setIsAdmin(claims.admin === 'true');
          setUserOrgId(claims.orgId || null);
          setUserFidClaim(claims.userFid || null);
          setUserMidClaim(claims.userMid || null);

          if (!claims.orgId) {
              console.warn("Auth Provider: User is missing orgId claim.");
          }

          // Smart sync: only call syncUserProfile if data has changed or is stale
          getDoc(doc(db, 'users', user.uid)).then(userDoc => {
            if (cancelled) return;
            const stored = userDoc.data();
            let needsSync = true;
            let reason = 'no existing profile';

            // Load member photo and name from user doc
            if (stored?.memberPhoto) {
              setMemberPhoto(stored.memberPhoto);
            }
            if (stored?.fullName) {
              setMemberName(stored.fullName);
            }

            if (stored && stored.lastRefreshed) {
              // Check if any auth values differ from what's stored
              const changes = [];
              if ((user.email || null) !== (stored.email || null)) changes.push('email');
              if (user.emailVerified !== stored.emailVerified) changes.push('emailVerified');
              if ((user.displayName || null) !== (stored.fullName || null)) changes.push('displayName');
              if ((user.photoURL || null) !== (stored.photoURL || null)) changes.push('photoURL');

              // Check claim values
              const storedAdmin = stored.isAdmin === true;
              const currentAdmin = claims.admin === 'true';
              if (currentAdmin !== storedAdmin) changes.push('admin');
              if ((claims.orgId || null) !== (stored.orgId || null)) changes.push('orgId');
              if ((claims.userMid || null) !== (stored.userMid || null)) changes.push('userMid');
              if ((claims.userFid || null) !== (stored.userFid || null)) changes.push('userFid');

              if (changes.length > 0) {
                reason = `changed: ${changes.join(', ')}`;
              } else {
                // No value changes - check if time-based refresh is needed
                const lastRefreshedMs = stored.lastRefreshed.seconds
                  ? stored.lastRefreshed.seconds * 1000
                  : (stored.lastRefreshed.toMillis ? stored.lastRefreshed.toMillis() : 0);
                const age = Date.now() - lastRefreshedMs;
                if (age <= SYNC_INTERVAL_MS) {
                  needsSync = false;
                } else {
                  reason = 'stale (>24h)';
                }
              }
            }

            if (needsSync) {
              console.log(`Auth Provider: Syncing profile (${reason})`);
              syncUserProfileCallable({ platform: 'web' })
                .then(() => console.log("Auth Provider: Profile sync completed"))
                .catch(err => console.warn("Auth Provider: Profile sync failed:", err.message));
            } else {
              console.log("Auth Provider: Profile up to date, skipping sync.");
            }
          }).catch(err => {
            if (cancelled) return;
            console.warn("Auth Provider: Could not check profile, syncing anyway:", err.message);
            syncUserProfileCallable({ platform: 'web' }).catch(() => {});
          });

        } catch (error) {
          if (cancelled) return;
          console.error("Auth Provider: Error fetching user claims:", error);
          setIsAdmin(false);
          setUserOrgId(null);
          setUserFidClaim(null);
        }
      } else {
        // No user, clear claims state
        setIsAdmin(false);
        setUserOrgId(null);
        setUserFidClaim(null);
        setUserMidClaim(null);
        setMemberPhoto(null);
        setMemberName(null);
      }
      setLoadingAuth(false);
    });

    // Cleanup subscription on unmount
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Value object provided to consuming components
  const value = {
    currentUser,
    isAdmin,
    userOrgId,
    userFidClaim,
    userMidClaim,
    memberPhoto,
    memberName,
    loadingAuth, // Provide loading state
  };

  // Render the provider with the value and children
  // Don't render children until initial auth check is done
  return (
    <AuthContext.Provider value={value}>
      {!loadingAuth && children}
    </AuthContext.Provider>
  );
}